<script lang="ts">
  import { getAppConfig } from '../../../common/config/app-config';
  import { getStr } from '../../../common/util';
  import ExtraButton from '../../common/components/ExtraButton.svelte';
  import Icon from '../../common/components/Icon.svelte';
  import Text from '../../common/components/Text.svelte';
  import { handleImageUploadCommon, uploadImages } from '../../common/services/imageUploadHelper';
  import { showSnackbar } from '../../common/store/snackbar_store';
  import { ServiceProduct } from '../mapper/home-maintenance.mapper';
  import {
    buildUpdatedService,
    createServiceVM,
    removeService,
    updateService,
  } from '../service/home-maintenance-data-services';
  import { cityUnavailable, serviceData } from '../store/home_maintenance_store';
  import HMServiceOptionPrice from './HMServiceOptionPrice.svelte';

  export let service: ServiceProduct;

  const { homeMaintenanceVipMembershipActive, lang, homeMaintenanceImageLimit } = getAppConfig();

  let showSuccessMessage = false;
  const MAX_IMAGES = 5;
  const imageSizeErrorMsg = getStr('extra.home.service.image.limit.msg', [homeMaintenanceImageLimit]);
  let isEditing = false;

  /* -----------------------------
     LOCAL STATE
  ------------------------------*/

  let description = '';
  let images: File[] = []; // new uploaded files
  let previews: string[] = []; // ALL visible images
  let existingImageUrls: string[] = []; // original backend images

  let originalDescription = '';
  let originalImageUrls: string[] = [];

  let isSaved = false;
  let hydratedExisting = false;
  let localChecked = false;

  /* -----------------------------
     STORE STATE
  ------------------------------*/

  $: existingService = $serviceData[service.productCode];
  $: isExisting = !!existingService?.isExisting;

  $: isChecked = isExisting || localChecked;
  $: isDisabled = isExisting || $cityUnavailable;

  /* -----------------------------
     DISPLAY RULES
  ------------------------------*/

  $: vm = createServiceVM({
    service,
    existingService,
  });

  /* -----------------------------
   VALIDATION
------------------------------*/

  $: isValidForSave = vm.validateSave({
    description,
    previews,
    originalDescription,
    originalImageUrls,
  });

  $: showSaveButton = vm.requiresSave;

  /* -----------------------------
     CHECKBOX
  ------------------------------*/

  function toggleCheck(e: Event) {
    if (isExisting || $cityUnavailable) return;

    const checked = (e.target as HTMLInputElement).checked;
    localChecked = checked;

    if (checked) {
      updateService(service.productCode, prev => ({
        ...prev, // preserve existing entryNumber + isExisting

        id: service.productCode,
        productCode: service.productCode,
        categoryCode: service.categoryCode,

        // preserve existing values if present
        description: prev?.description ?? '',
        imageUrls: prev?.imageUrls ?? [],

        isExisting: prev?.isExisting ?? false,
        entryNumber: prev?.entryNumber,

        isSaved: false,
        isModified: false,
      }));

      if (!vm.requiresSave) {
        isSaved = true;

        updateService(service.productCode, prev => ({
          ...prev,
          isSaved: true,
          isModified: true,
        }));
      }
    } else {
      removeService(service.productCode);
      resetLocalState();
    }
  }

  function resetLocalState() {
    description = '';
    images = [];
    previews = [];
    existingImageUrls = [];
    originalImageUrls = [];
    originalDescription = '';
    isSaved = false;
  }

  /* -----------------------------
     IMAGE UPLOAD
  ------------------------------*/

  function handleImageUpload(event: Event) {
    const result = handleImageUploadCommon(event, images, previews, MAX_IMAGES, {
      allowedTypes: ['image/png', 'image/jpeg', 'image/jpg'],
      maxFileSizeMB: homeMaintenanceImageLimit,
    });

    if (result?.error && result.isFileTooLarge) {
      showSnackbar(getStr(imageSizeErrorMsg), 'error');
      return;
    }

    if (result.error) return;

    images = result.images;

    previews = result.previews;
  }

  function removeImage(index: number) {
    previews = previews.filter((_, i) => i !== index);

    if (index < originalImageUrls.length) {
      existingImageUrls = existingImageUrls.filter((_, i) => i !== index);
    } else {
      const newIndex = index - originalImageUrls.length;
      images = images.filter((_, i) => i !== newIndex);
    }
  }

  /* -----------------------------
     SAVE
  ------------------------------*/

  async function saveService() {
    try {
      window['ACC']?.loader?.showLoader();

      const uploadedUrls = vm.hasUpload ? await uploadImages(images) : [];

      const finalImageUrls = vm.buildFinalImages(existingImageUrls, uploadedUrls);
      serviceData.update(data => ({
        ...data,
        [service.productCode]: buildUpdatedService({
          prev: data[service.productCode],
          description,
          imageUrls: finalImageUrls,
          entryNumber: existingService?.entryNumber,
          isExisting: existingService?.isExisting,
        }),
      }));

      originalDescription = description;
      originalImageUrls = [...finalImageUrls];

      images = [];
      existingImageUrls = [...finalImageUrls];
      previews = [...finalImageUrls];

      isSaved = true;
      isEditing = false;

      showSuccessMessage = true;
      setTimeout(() => {
        showSuccessMessage = false;
      }, 2000);
    } finally {
      window['ACC']?.loader?.hideLoader();
    }
  }

  function editService() {
    isSaved = false;
    isEditing = true;

    originalDescription = description;
    originalImageUrls = [...previews];

    // Mark as not saved in store to block ATC
    updateService(service.productCode, prev => ({
      ...prev,
      isSaved: false,
    }));
  }

  /* -----------------------------
     HYDRATE EXISTING
  ------------------------------*/

  $: if (isExisting && !hydratedExisting) {
    hydrateFromExisting();
  }

  function hydrateFromExisting() {
    description = existingService.description || '';
    existingImageUrls = existingService.imageUrls || [];

    previews = [...existingImageUrls];

    originalDescription = description;
    originalImageUrls = [...existingImageUrls];

    isSaved = true;
    hydratedExisting = true;
    localChecked = true;

    updateService(service.productCode, prev => ({
      ...prev,
      isSaved: true,
      isModified: false,
    }));
  }

  $: emptySlots = MAX_IMAGES - previews.length;
</script>

<div class="service-card {isSaved ? 'saved' : ''}">
  <div class="header">
    <label class="checkbox-wrapper">
      <div class="d-flex align-items-center">
        <input type="checkbox" checked={isChecked} disabled={isDisabled} on:change={toggleCheck} />
        <span class="custom-checkbox {isDisabled ? 'disabled' : ''}"></span>
        <span class="title">{lang === 'en' ? service?.product?.nameEn : service?.product?.name}</span>
        <img src={service?.iconUrl} alt="" />
      </div>
      <p class="service-desc">{service?.description || service?.product?.description}</p>
    </label>

    <div class="right-section">
      <HMServiceOptionPrice {service} {isSaved} {editService} isVipMember={homeMaintenanceVipMembershipActive} />
    </div>
  </div>

  {#if isChecked}
    {#if showSuccessMessage}
      <div class="success-message">
        <span class="c_icon_new c_icon_new--check"></span>
        <span class="message">
          <Text key="extra.home.service.saved.message" />
        </span>
      </div>
    {/if}
    {#if !isSaved}
      <div class="details">
        <!-- svelte-ignore a11y-label-has-associated-control -->
        {#if vm.hasDesc}
          <label class="desc-label">
            <Text key="extra.home.service.device.symptoms.text" />
          </label>

          <textarea placeholder="" bind:value={description} disabled={isSaved} maxlength="400" />
        {/if}
        {#if vm?.hasUpload}
          <div class="image-upload">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="upload-label">
              <Text key="extra.home.service.upload.image" />
            </label>

            <div class="thumbnails">
              <!-- Uploaded previews -->
              {#each previews as img, index}
                <div class="thumb">
                  <img src={img} alt="preview" />
                  <!-- svelte-ignore a11y-click-events-have-key-events -->
                  <div class="remove-btn" on:click={() => removeImage(index)}>
                    <Icon type="font" glyph="close" mobileSize={9} desktopSize={15} />
                  </div>
                </div>
              {/each}

              <!-- Placeholders -->
              {#each Array(emptySlots) as _}
                <label class="thumb placeholder {isSaved ? 'disabled' : ''}">
                  <input type="file" accept="image/*" hidden disabled={isSaved} on:change={handleImageUpload} />
                  <Icon glyph="icon-thumb-placeholder" type="primary" mobileSize={24} desktopSize={24} />
                </label>
              {/each}
            </div>
          </div>
        {/if}
        <div class="actions">
          {#if showSaveButton && !isSaved}
            <ExtraButton
              variant="solid"
              disabled={!isValidForSave && !isEditing}
              color="secondary"
              size="small"
              on:click={saveService}
            >
              <Text key="extra.home.service.save.btn.text" />
            </ExtraButton>
          {/if}
        </div>
      </div>
    {/if}
  {/if}
</div>

<style lang="scss">
  @import '../../common/styles/colors.new.scss';
  .service-card {
    border: 1px solid $primary-white;
    border-radius: 8px;
    padding: 12px 8px;
    background: $primary-white;
    gap: 8px;
    display: flex;
    flex-direction: column;
  }
  .service-card.saved {
    border: 1px solid $functional-green-03;
  }
  .header {
    display: flex;
    align-items: flex-start;
    gap: 4px;
  }

  .checkbox-wrapper {
    display: flex;
    cursor: pointer;
    gap: 8px;
    flex-direction: column;
    width: 65%;

    @include desktop-max-width {
      width: 70%;
    }

    .d-flex {
      gap: 8px;
    }
  }

  .checkbox-wrapper input {
    display: none;
  }

  /* unchecked */
  .custom-checkbox {
    width: 18px;
    height: 18px;
    border: 2px solid $primary-blue;
    position: relative;
    box-sizing: border-box;
    background: $primary-white;
    &.disabled {
      background: rgba(148, 147, 152, 0.3);
      border: 1px solid rgba(148, 147, 152, 1);
      pointer-events: none;
    }
  }

  /* checked background */
  .checkbox-wrapper input:checked + .custom-checkbox {
    background-color: $primary-blue;
    border-color: $primary-blue;
  }

  .checkbox-wrapper input:checked + .disabled {
    background: rgba(148, 147, 152, 0.3);
    border: 1px solid rgba(148, 147, 152, 1);
    pointer-events: none;
  }

  /* white tick */
  .checkbox-wrapper input:checked + .custom-checkbox::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 2px;
    width: 5px;
    height: 9px;
    border: solid $primary-white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
  .title {
    font-weight: 700;
    font-size: 16px;
    line-height: 16px;
    color: $neutral-dark-01;
    text-align: end;
    @include desktop-max-width {
      font-size: 14px;
    }
  }

  .service-desc {
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    margin: 0;
    color: $neutral-dark-01;
  }

  .details {
    border: 1px solid $primary-blue;
    padding: 10px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .thumbnails {
    display: flex;
    gap: 4px;
    .thumb {
      position: relative;
      max-height: 54px;
      img {
        height: 54px;
        width: 80px;
      }
      .remove-btn {
        position: absolute;
        top: 2px;
        right: 4px;
        color: $icon-only-icon-blue;
      }
    }
  }
  .right-section {
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: flex-end;
    width: 35%;
    :global {
      .price-details {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .current-price {
        font-weight: 700;
        font-size: 16px;
        line-height: 16px;
        color: $neutral-dark-01;
        text-align: end;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 2px;
        .c_icon_new {
          font-size: 15px;
        }
      }
      .old-price {
        display: flex;
        gap: 4px;
        font-size: 12px;
        line-height: 14px;
        align-items: center;
        .old-amount {
          text-decoration: line-through;
          color: $neutral-dark-03;
        }
        .saved-amount {
          font-weight: 700;
          color: $functional-orange;
        }
      }
    }
  }

  textarea {
    width: 100%;
    min-height: 160px;
    padding: 8px;
    resize: none;
    border-radius: 8px;
    border: 1px solid $neutral-dark-05;
  }

  input[type='file'] {
    margin-top: 6px;
  }

  .preview {
    display: flex;
    gap: 8px;
    margin-top: 6px;
  }

  .preview img {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    object-fit: cover;
    border: 1px solid #ccc;
  }

  .actions {
    margin-top: 8px;
    width: fit-content;
  }

  .edit-inline-btn {
    font-size: 20px;
    color: $icon-only-icon-blue;
    cursor: pointer;
  }
  .save-btn,
  .edit-btn {
    padding: 6px 16px;
    border-radius: 20px;
    border: none;
    cursor: pointer;
  }

  .save-btn {
    background-color: #2563eb;
    color: white;
  }

  .save-btn:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  .edit-btn {
    background-color: #666;
    color: white;
  }
  .success-message {
    background-color: $functional-green-03;
    color: $neutral-dark-01;
    border-radius: 8px;
    padding: 12px 24px;
    margin-top: 8px;
    font-weight: 500;
    font-size: 18px;
    line-height: 20px;
    display: flex;
    gap: 8px;
    align-items: center;
    .c_icon_new {
      font-size: 32px;
      color: $tertiary-blue-02;
    }
  }
</style>
